<template>
    <base-component>
        <template slot="form">
            <person-search-form :fetch="fetchData" :pageSize="pageSize" :offset="offset" :setOffset="setOffset"/>
        </template>
        <template slot="createButton">
            <b-button v-if="!showCreatePerson" @click="toggleCreatePerson" variant="primary">
                <account-plus-icon title="" />&nbsp;{{ $t('general.add') }}
            </b-button>
        </template>
        <template slot="createForm">
            <person-create-form v-if="showCreatePerson" :to-person-list="fetchData" :close-form="toggleCreatePerson" />
        </template>
        <template slot="workArea">
            <b-alert variant="danger" :show="showAlert">
                {{ alert }}
            </b-alert>
            <b-card class="flex-fill flex-shrink-0">
                <pagination :pageSize="this.pageSize" :offset="offset" :listLength="users.length" :setOffset="setOffset"/>
                <b-table show-empty striped small :items="users" :fields="fields" class="table-borderless">
                    <template slot="registrationDate" slot-scope="row">
                        {{ $d(new Date(row.item.registrationDate), 'short') }}
                    </template>
                    <template slot="authType" slot-scope="row">
                        <span>{{ $t(`person.authTypes.${row.item.authType}`) }}</span>
                    </template>
                    <template slot="phone" slot-scope="row">
                        <input type="tel" v-mask="'+7 (7##) ###-##-##'" :value="row.item.phone" class="bg-transparent border-0" readonly />
                    </template>
                    <template slot="isActive" slot-scope="row">
                        <b-form-checkbox :checked="row.item.isActive" disabled></b-form-checkbox>
                    </template>
                    <template slot="action" slot-scope="row">
                        <b-dropdown v-if="(isAdmin &&
                                (row.item.role == $appConfig.role.admin || row.item.role == $appConfig.role.operator)) ||
                                (isOperator &&
                                (row.item.role == $appConfig.role.dealer || row.item.role == $appConfig.role.inspector))"
                                variant="link">
                            <template slot="button-content">
                                <dots-icon title="" />
                            </template>
                            <b-dd-item @click.stop="openEditPersonModal(row)">{{ $t('general.edit') }}</b-dd-item>
                            <b-dd-item @click.stop="openChangePasswordModal(row)">{{ $t('menu.changePassword') }}</b-dd-item>
                            <b-dd-item v-show="row.item.role == $appConfig.role.dealer || row.item.role == $appConfig.role.inspector"
                                @click.stop="openSetDealerModal(row)">{{ $t('menu.setDealer') }}</b-dd-item>
                            <b-dd-item v-show="row.item.autType !== 100" @click.stop="openRevokeCertConfirmModal(row)">
                                {{ $t('menu.revokeCert') }}
                            </b-dd-item>
                        </b-dropdown>
                    </template>
                    <template slot="empty">
                        <div class="text-center">
                            {{ $t('general.noData') }}
                        </div>
                    </template>
                </b-table>
                <b-modal ref="editPersonModal" :title="$t('menu.editPerson')" hide-footer lazy>
                    <person-edit-form :person-id="clickedPerson.id" />
                </b-modal>
                <b-modal ref="changePasswordModal" :title="$t('menu.changePassword')" hide-footer lazy>
                    <person-change-password-form :person-id="clickedPerson.id" />
                </b-modal>
                <b-modal ref="setDealerModal" :title="$t('menu.setDealer')" hide-footer lazy>
                    <person-set-dealer-form :login="clickedPerson.login" :dealerId="clickedPerson.dealer"/>
                </b-modal>
                <b-modal ref="certRevokeConfirmModal" :title="$t('menu.revokeCert')"
                    @ok="revokeCert" :cancel-title="$t('general.cancel')" lazy>
                    <p>{{ $t('person.certRevokeConfirmation', [clickedPerson.login]) }}</p>
                </b-modal>
                <b-modal ref="certRevokedModal" :title="$t('menu.revokeCert')" ok-only lazy>
                    <b-alert variant="success" show>{{ $t('person.certRevokedMessage') }}</b-alert>
                </b-modal>
                <b-modal ref="certRevokedErrorModal" :title="$t('menu.revokeCert')" ok-only lazy>
                    <b-alert variant="danger" show>{{ alert }}</b-alert>
                </b-modal>
            </b-card>
        </template>
    </base-component>
</template>

<script>
/* eslint-disable */
const PersonSearchForm = () => import(/* webpackChunkName: "chunk-views" */ '@/components/person/PersonSearchForm.vue')
const PersonCreateForm = () => import(/* webpackChunkName: "chunk-views" */ '@/components/person/PersonCreateForm.vue')
const PersonEditForm = () => import(/* webpackChunkName: "chunk-views" */ '@/components/person/PersonEditForm.vue')
const PersonChangePasswordForm = () => import(/* webpackChunkName: "chunk-views" */ '@/components/person/PersonChangePasswordForm.vue')
const PersonSetDealerForm = () => import(/* webpackChunkName: "chunk-views" */ '@/components/person/PersonSetDealerForm.vue')
const Pagination = () => import(/* webpackChunkName: "chunk-views" */ '@/components/general/Pagination.vue')

export default {
    name: 'Persons',
    data () {
        return {
            showAlert: false,
            alert: '',
            showCreatePerson: false,
            clickedPerson: {},
            users: [],
            pageSize: 20,
            offset: 0
        }
    },
    components: {
        PersonSearchForm,
        PersonCreateForm,
        PersonEditForm,
        PersonChangePasswordForm,
        PersonSetDealerForm,
        Pagination
    },
    computed: {
        fields: function () {
            return [
                { key: 'id', label: 'ID', sortable: true },
                { key: 'login', label: this.$t('person.login'), sortable: true },
                { key: 'surname', label: this.$t('person.lastname'), sortable: true },
                { key: 'firstName', label: this.$t('person.firstname'), sortable: true },
                { key: 'patronymic', label: this.$t('person.middlename') },
                { key: 'registrationDate', label: this.$t('person.regdate') },
                { key: 'phoneNumber', label: this.$t('person.phone') },
                { key: 'authType', label: this.$t('person.authType'), sortable: true },
                { key: 'isActive', label: this.$t('person.enabled'), sortable: true },
                { key: 'action', label: this.$t('general.actions') }
            ]
        },
        isAdmin: function () {
            return this.$store.getters.getUserRole === this.$appConfig.role.admin
        },
        isOperator: function () {
            return this.$store.getters.getUserRole === this.$appConfig.role.operator
        }
    },
    methods: {
        fetchData: function (formData) {
            this.hideAlert();

            this.$request.sendRequest(
                this, 
                this.$appConfig.apiUrls.person.getAll,
                formData, 
                result => this.users = result, 
                this.displayAlert)
        },
        hideAlert: function () {
            this.showAlert = false
            this.alert = ''
        },
        displayAlert: function (errorCode) {
            this.alert = this.$i18n.t(`errors.${errorCode}`)
            this.showAlert = true
        },
        toggleCreatePerson: function () {
            this.hideAlert()
            this.showCreatePerson = !this.showCreatePerson
        },
        openEditPersonModal: function (row) {
            this.clickedPerson = row.item
            this.$refs.editPersonModal.show()
        },
        openChangePasswordModal: function (row) {
            this.clickedPerson = row.item
            this.$refs.changePasswordModal.show()
        },
        openSetDealerModal: function (row) {
            this.clickedPerson = row.item
            this.$refs.setDealerModal.show()
        },
        openRevokeCertConfirmModal: function (row) {
            this.clickedPerson = row.item
            this.$refs.certRevokeConfirmModal.show()
        },
        revokeCert: function (row) {
            this.$request.sendRequest(this, this.$appConfig.apiUrls.person.revokeCert,
                { login: this.clickedPerson.login }, () => {
                    this.fetchData()
                    this.$refs.certRevokedModal.show()
                }, error => {
                    this.alert = this.$t(`errors.${error}`)
                    this.$refs.certRevokedErrorModal.show()
                })
        },
        setOffset: function (newValue) {
            this.offset = newValue
        }
    }
}
</script>
